import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./useAuth";

/**
 * Available feature flags in the system. Must stay in sync with the backend
 * union in featureFlagService.ts.
 *
 * additional_meals — breakfast/lunch planning beyond the dinner baseline.
 * Gates the wizard toggles (creation); existing plans with breakfast/lunch
 * assignments render regardless of the flag.
 */
export type FeatureFlag = "additional_meals";

export type FeatureFlags = Record<FeatureFlag, boolean>;

/** ISO timestamps of when each flag was last turned ON globally */
export type FlagEnabledAt = Partial<Record<FeatureFlag, string>>;

type FeatureFlagsResponse = {
  featureFlags: FeatureFlags;
  flagEnabledAt: FlagEnabledAt;
};

const featureFlagsQueryOptions = (user: ReturnType<typeof useAuth>["user"]) => ({
  queryKey: ["featureFlags", user?.uid] as const,
  queryFn: async (): Promise<FeatureFlagsResponse> => {
    if (!user) {
      // Not authenticated: no flags to resolve
      return { featureFlags: {} as FeatureFlags, flagEnabledAt: {} };
    }

    const idToken = await user.getIdToken();

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/user/feature-flags`,
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch feature flags");
    }

    const data = await response.json();
    return {
      featureFlags: data.featureFlags ?? {},
      flagEnabledAt: data.flagEnabledAt ?? {},
    };
  },
  enabled: !!user,
  staleTime: 5 * 60 * 1000, // Cache for 5 minutes
});

/**
 * Hook to get feature flags for the current user. Shares one cached fetch
 * with useFlagEnabledAt via select, preserving the flags-map contract every
 * existing consumer relies on.
 */
export const useFeatureFlags = () => {
  const { user } = useAuth();
  return useQuery({
    ...featureFlagsQueryOptions(user),
    select: (data: FeatureFlagsResponse) => data.featureFlags,
  });
};

/**
 * When each flag last went ON globally (absent = never). Drives the
 * announcement modal's new-account cutoff: the global flip is the moment a
 * feature becomes baseline for new signups, so accounts created after it
 * never see that feature's announcement, while accounts created before it
 * (including per-user flagged testers during a staged rollout) always can.
 */
export const useFlagEnabledAt = () => {
  const { user } = useAuth();
  return useQuery({
    ...featureFlagsQueryOptions(user),
    select: (data: FeatureFlagsResponse) => data.flagEnabledAt,
  });
};

/**
 * Hook to check if a specific feature flag is enabled
 */
export const useFeatureFlag = (flag: FeatureFlag): boolean => {
  const { data: featureFlags } = useFeatureFlags();
  return featureFlags?.[flag] ?? false;
};

/**
 * Hook to check if a specific feature flag is enabled with loading state
 */
export const useFeatureFlagWithLoading = (
  flag: FeatureFlag
): { enabled: boolean; loading: boolean } => {
  const { data: featureFlags, isLoading } = useFeatureFlags();
  return {
    enabled: featureFlags?.[flag] ?? false,
    loading: isLoading,
  };
};
